import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "../../styles/pages/games/TypeSprint.module.scss";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faBolt,
  faBullseye,
  faTrophy,
  faPause,
  faPlay,
  faRotateRight,
  faVolumeHigh,
  faVolumeXmark,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

const PACKS = {
  general: [
    "creative","portfolio","design","neon","canvas","project","component","react",
    "module","layout","accessibility","responsive","function","optimize","performance",
    "keyboard","mobile","animation","particle","gradient","shadow","grid","flex",
    "variable","async","api","router","state","storage","semantic","heading","footer",
    "button","dialog","modal","render","cursor","scroll","pointer","focus","iterate",
    "string","number","boolean","object","array","event","listener","handler","refactor"
  ],
};

function makeSequence(len = 80, pack = "general") {
  const words = PACKS[pack] || PACKS.general;
  const arr = [];
  for (let i = 0; i < len; i++) arr.push(words[(Math.random() * words.length) | 0]);
  return arr;
}

/* ------------------ WebAudio SFX (no files) ------------------ */
function createSfx() {
  let ctx = null;
  let master = null;

  const state = {
    enabled: true,
    lastKeyAt: 0,
  };

  const ensure = async () => {
    if (!ctx) {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      master = ctx.createGain();
      master.gain.value = 0.20;
      master.connect(ctx.destination);
    }
    if (ctx.state === "suspended") await ctx.resume();
  };

  const beep = async ({
    f = 440,
    d = 0.05,
    type = "square",
    gain = 0.7,
    attack = 0.005,
    release = 0.06,
  } = {}) => {
    if (!state.enabled) return;
    await ensure();
    if (!ctx || !master) return;

    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.value = f;
    o.connect(g);
    g.connect(master);

    const t = ctx.currentTime;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(gain, t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + d + release);

    o.start(t);
    o.stop(t + d + release + 0.02);
  };

  const noise = async ({ d = 0.07, gain = 0.35 } = {}) => {
    if (!state.enabled) return;
    await ensure();
    if (!ctx || !master) return;

    const bufferSize = Math.max(1, (ctx.sampleRate * d) | 0);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * 0.8;

    const src = ctx.createBufferSource();
    src.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = "highpass";
    filter.frequency.value = 1200;

    const g = ctx.createGain();
    g.gain.value = gain;

    src.connect(filter);
    filter.connect(g);
    g.connect(master);

    src.start();
    src.stop(ctx.currentTime + d + 0.02);
  };

  return {
    setEnabled: (v) => (state.enabled = v),
    getEnabled: () => state.enabled,

    /* 80s-ish “key click”: mix a tiny square beep + a whisper of noise */
    key: async () => {
      const now = performance.now();
      if (now - state.lastKeyAt < 18) return; // rate-limit
      state.lastKeyAt = now;
      await beep({ f: 900 + Math.random() * 140, d: 0.022, type: "square", gain: 0.18, release: 0.03 });
      await noise({ d: 0.015, gain: 0.06 });
    },

    correct: async () => {
      await beep({ f: 660, d: 0.05, type: "triangle", gain: 0.35 });
      await beep({ f: 880, d: 0.06, type: "triangle", gain: 0.32 });
    },

    wrong: async () => {
      await beep({ f: 180, d: 0.08, type: "sawtooth", gain: 0.30 });
      await noise({ d: 0.06, gain: 0.22 });
    },

    start: async () => {
      await beep({ f: 440, d: 0.05, type: "triangle", gain: 0.30 });
      await beep({ f: 660, d: 0.05, type: "triangle", gain: 0.32 });
      await beep({ f: 880, d: 0.07, type: "triangle", gain: 0.34 });
    },

    end: async () => {
      await beep({ f: 523, d: 0.07, type: "triangle", gain: 0.30 });
      await beep({ f: 392, d: 0.07, type: "triangle", gain: 0.26 });
      await beep({ f: 330, d: 0.09, type: "triangle", gain: 0.24 });
    },

    unlock: ensure,
  };
}

export default function TypeSprint({ seconds = 60, pack = "general", onExit }) {
  const sfxRef = useRef(null);
  if (!sfxRef.current) sfxRef.current = createSfx();

  const [running, setRunning] = useState(true);
  const [time, setTime] = useState(seconds);

  const [seq, setSeq] = useState(() => makeSequence(80, pack));
  const [idx, setIdx] = useState(0);

  const [input, setInput] = useState("");
  const [correct, setCorrect] = useState(0);
  const [mistakes, setMistakes] = useState(0);

  const [best, setBest] = useState(Number(localStorage.getItem("typesprint_best") || 0));
  const [soundOn, setSoundOn] = useState(true);

  const inputRef = useRef(null);

  const current = seq[idx] || "";
  const typedOk = useMemo(() => input && current.startsWith(input), [input, current]);

  // Timer
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      setTime((t) => {
        if (t <= 1) {
          setRunning(false);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [running]);

  // Focus on mount/open
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // bind sound enabled
  useEffect(() => {
    sfxRef.current?.setEnabled(soundOn);
  }, [soundOn]);

  // Metrics
  const wordsTyped = correct + mistakes;
  const charsTyped = useMemo(() => {
    let total = 0;
    for (let i = 0; i < idx; i++) total += seq[i].length + 1;
    total += input.length;
    return total;
  }, [idx, input, seq]);

  const elapsed = Math.max(1, seconds - time); // avoid divide by 0
  const accuracy = wordsTyped ? Math.round((correct / wordsTyped) * 100) : 100;
  const wpm = Math.round((charsTyped / 5) / (elapsed / 60));

  // Save best when finished + play end jingle once
  const endedRef = useRef(false);
  useEffect(() => {
    if (time !== 0) return;
    if (!endedRef.current) {
      endedRef.current = true;
      sfxRef.current?.end();
    }
    setBest((b) => {
      const next = Math.max(b, wpm);
      localStorage.setItem("typesprint_best", String(next));
      return next;
    });
  }, [time, wpm]);

  // ---- Submit logic ----
  function advance(val) {
    if (val === current) {
      setCorrect((c) => c + 1);
      sfxRef.current?.correct();
    } else {
      setMistakes((m) => m + 1);
      sfxRef.current?.wrong();
    }

    setIdx((i) => i + 1);
    setInput("");

    if (idx + 10 > seq.length) setSeq((s) => [...s, ...makeSequence(40, pack)]);
  }

  function handleChange(e) {
    const raw = e.target.value;

    // play key sound only when character count increases
    if (raw.length > input.length) sfxRef.current?.key();

    // mobile: space = submit
    if (/\s/.test(raw)) {
      const word = raw.trim();
      if (word) advance(word);
      return;
    }

    setInput(raw);
  }

  function onKeyDown(e) {
    if (e.key === " " || e.code === "Space") {
      e.preventDefault();
      const word = input.trim();
      if (word) advance(word);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const word = input.trim();
      if (word) advance(word);
      return;
    }

    // also make backspace quiet (optional). If you want, add a different sound here.
    // if (e.key === "Backspace") ...
  }

  function restart() {
    endedRef.current = false;
    setRunning(true);
    setTime(seconds);
    setSeq(makeSequence(80, pack));
    setIdx(0);
    setInput("");
    setCorrect(0);
    setMistakes(0);
    inputRef.current?.focus();
    sfxRef.current?.start();
  }

  function toggleRunning() {
    // unlock audio on first user gesture
    sfxRef.current?.unlock();
    setRunning((r) => !r);
  }

  function toggleSound() {
    sfxRef.current?.unlock();
    setSoundOn((s) => !s);
  }

  return (
    <div className={styles.wrap} onMouseDown={() => inputRef.current?.focus()}>
      {/* Header */}
      <header className={styles.top}>
        <div className={styles.brand}>
          <div className={styles.title}>Type Sprint</div>
          <div className={styles.sub}>Speed • Accuracy • Focus</div>
        </div>

        <div className={styles.hud} aria-label="Session stats">
          <div className={styles.hudChip}>
            <FontAwesomeIcon icon={faClock} className={styles.hudIcon} />
            <span>{time}s</span>
          </div>

          <div className={styles.hudChip}>
            <FontAwesomeIcon icon={faBolt} className={styles.hudIcon} />
            <span>WPM: {Number.isFinite(wpm) ? wpm : 0}</span>
          </div>

          <div className={styles.hudChip}>
            <FontAwesomeIcon icon={faBullseye} className={styles.hudIcon} />
            <span>{accuracy}%</span>
          </div>

          <div className={styles.hudChip}>
            <FontAwesomeIcon icon={faTrophy} className={styles.hudIcon} />
            <span>Best: {best}</span>
          </div>
        </div>

        <div className={styles.actions}>
          <button className={styles.btn} type="button" onClick={toggleRunning}>
            <FontAwesomeIcon icon={running ? faPause : faPlay} />
            <span>{running ? "Pause" : "Resume"}</span>
          </button>

          <button className={styles.btn} type="button" onClick={restart}>
            <FontAwesomeIcon icon={faRotateRight} />
            <span>Restart</span>
          </button>

          <button className={`${styles.btn} ${styles.iconBtn}`} type="button" onClick={toggleSound} aria-label="Toggle sound">
            <FontAwesomeIcon icon={soundOn ? faVolumeHigh : faVolumeXmark} />
          </button>

          {onExit && (
            <button className={`${styles.btn} ${styles.iconBtn}`} type="button" onClick={onExit} aria-label="Exit">
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </div>
      </header>

      {/* Stage */}
      <main className={styles.stage}>
        {time > 0 ? (
          <>
            <div className={styles.words} aria-live="polite">
              {seq.slice(Math.max(0, idx - 6), idx).map((w, i) => (
                <span key={`p-${i}`} className={styles.past}>{w}</span>
              ))}

              <span className={styles.current}>
                <mark className={typedOk ? styles.ok : styles.bad}>{input || ""}</mark>
                <span className={styles.rest}>{current.slice(input.length)}</span>
              </span>

              {seq.slice(idx + 1, idx + 12).map((w, i) => (
                <span key={`n-${i}`} className={styles.next}>{w}</span>
              ))}
            </div>

            <div className={styles.entryRow}>
              <input
                ref={inputRef}
                className={styles.input}
                value={input}
                onChange={handleChange}
                onKeyDown={onKeyDown}
                placeholder="Type… then Space / Enter"
                autoCapitalize="off"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                disabled={!running}
              />
              <button className={styles.submit} type="button" onClick={() => input.trim() && advance(input.trim())} disabled={!running}>
                Enter
              </button>
            </div>

            <p className={styles.tip}>
              Space/Enter submits • WPM uses 5-char metric • Subtle 80s key-click audio
            </p>
          </>
        ) : (
          <div className={styles.overWrap}>
            <div className={styles.overCard}>
              <div className={styles.overHeader}>
                <div>
                  <div className={styles.overKicker}>Session Complete</div>
                  <div className={styles.overTitle}>Time Up</div>
                  <div className={styles.overSub}>Performance summary</div>
                </div>
              </div>

              <div className={styles.overGrid}>
                <div className={styles.overStat}>
                  <div className={styles.k}>WPM</div>
                  <div className={styles.v}>{Number.isFinite(wpm) ? wpm : 0}</div>
                </div>

                <div className={styles.overStat}>
                  <div className={styles.k}>Accuracy</div>
                  <div className={styles.v}>{accuracy}%</div>
                </div>

                <div className={styles.overStat}>
                  <div className={styles.k}>Correct</div>
                  <div className={styles.v}>{correct}</div>
                </div>

                <div className={styles.overStat}>
                  <div className={styles.k}>Mistakes</div>
                  <div className={styles.v}>{mistakes}</div>
                </div>

                <div className={styles.overStatWide}>
                  <div className={styles.k}>Best WPM</div>
                  <div className={styles.v}>{best}</div>
                </div>
              </div>

              <div className={styles.overActions}>
                <button className={`${styles.btn} ${styles.primary}`} type="button" onClick={restart}>
                  <FontAwesomeIcon icon={faRotateRight} />
                  <span>Play again</span>
                </button>

                <button className={styles.btn} type="button" onClick={() => { setTime(seconds); setRunning(true); inputRef.current?.focus(); }}>
                  <FontAwesomeIcon icon={faPlay} />
                  <span>Resume (no reset)</span>
                </button>
              </div>

              <div className={styles.overNote}>
                Tip: aim for rhythm. Clean accuracy beats frantic speed.
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
